import { useEffect, useState } from 'react';
import { Button } from '@safetyhub/ui';
import { formatDateTimeISO, parseLocalToUTC } from '@safetyhub/shared';
import Modal from '../../../components/Modal';
import { useDirtySnapshot } from '../../../hooks/useDirtySnapshot';
import {
  ORG_ANNOUNCEMENT_PLATFORMS,
  type AnnouncementPopupOptionsInput,
  type OrgAnnouncement,
  type OrgAnnouncementInput,
  type OrgAnnouncementPlatform,
} from '../../../lib/announcements';
import {
  DEFAULT_POPUP_OPTIONS,
  POPUP_FREQUENCIES,
  POPUP_FREQUENCY_LABELS,
  POPUP_OPT_OUTS,
  POPUP_OPT_OUT_LABELS,
  toPopupOptions,
} from './announcementPopupOptions';

/** surface 화면 문구 — 이 화면이 보낼 수 있는 둘 전부에 대해 exhaustive하다 */
const PLATFORM_LABELS: Record<OrgAnnouncementPlatform, string> = {
  WEB: '웹',
  APP: '앱(모바일)',
};

/**
 * 기간 한 칸의 body 조각 — 값이면 ISO, 빈 칸은 생성이면 키 생략 · 수정이면 `null`(지움).
 * 서버 `UpdateAnnouncementDto`에서 생략은 「유지」라 수정의 빈 칸을 생략으로 보내면 지워지지 않는다.
 */
function exposureBoundField(
  key: 'startsAt' | 'endsAt',
  value: Date | null,
  isEdit: boolean,
): Partial<Pick<OrgAnnouncementInput, 'startsAt' | 'endsAt'>> {
  if (value) return { [key]: value.toISOString() };
  return isEdit ? { [key]: null } : {};
}

interface AnnouncementFormModalProps {
  open: boolean;
  /** 수정 대상 — null이면 생성 */
  target: OrgAnnouncement | null;
  saving: boolean;
  onClose: () => void;
  onSubmit: (input: OrgAnnouncementInput) => Promise<void>;
}

/**
 * 조직 공지 저작 폼 — 생성·수정 한 벌.
 *
 * ## 시각은 **현지 입력 → UTC 전송**이다
 *
 * `datetime-local` 값은 현지 벽시계이고 서버는 ISO 8601 UTC를 받으므로 shared
 * `parseLocalToUTC`(기본 `Asia/Seoul`)를 거친다 — **문자열을 그대로 보내지 않는다**(CLAUDE.md
 * 시간대 정책). 되돌려 채울 때는 `formatDateTimeISO`이고, 분 단위로 잘라 넣는다.
 *
 * ## 서버가 **거부하는 조합을 고를 수 없게** 둔다
 *
 * - `platforms`는 **최소 하나** — 빈 배열은 「아무에게도 안 보이는데 저장은 성공」이라 서버가
 *   400(`ANNOUNCEMENT_PLATFORMS_REQUIRED`)으로 막는다. 여기서도 제출을 막아 그 왕복을 없앤다.
 * - `ADMIN`은 **목록에 없다** — 조직 공지가 담을 수 없는 값이다(`ORG_ANNOUNCEMENT_PLATFORMS` JSDoc).
 * - 게시 시작 ≥ 종료는 **노출 창이 비는** 상태라 서버가 400(`ANNOUNCEMENT_PERIOD_EMPTY`)이다.
 *
 * ⚠️ `audiences`(역할 좁히기)는 **이 폼에 없다** — 생략이 「그 surface의 모두」이고 조직 공지는 그
 * 조직 전체가 대상이다. 열려면 「좁히지 않은 체계」 명시 승인 축까지 함께 와야 한다(그 DTO JSDoc).
 */
export default function AnnouncementFormModal({
  open,
  target,
  saving,
  onClose,
  onSubmit,
}: AnnouncementFormModalProps) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [platforms, setPlatforms] = useState<OrgAnnouncementPlatform[]>(['WEB']);
  const [pinned, setPinned] = useState(false);
  const [publish, setPublish] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  /** 「팝업으로도 알리기」 — 결정 2026-10-26 개정 Q4. 켜면 두 옵션이 보인다 */
  const [popupEnabled, setPopupEnabled] = useState(false);
  const [popupOptions, setPopupOptions] = useState<AnnouncementPopupOptionsInput>(DEFAULT_POPUP_OPTIONS);
  const [formError, setFormError] = useState<string | null>(null);
  const [baselinePending, setBaselinePending] = useState(false);

  // 열릴 때마다 대상 기준으로 초기화한다 — 닫고 다시 열면 지난 입력이 남지 않는다.
  useEffect(() => {
    if (!open) return;
    setFormError(null);
    setTitle(target?.title ?? '');
    setBody(target?.body ?? '');
    setPlatforms(
      target
        ? ORG_ANNOUNCEMENT_PLATFORMS.filter((p) => target.platforms.includes(p))
        : ['WEB'],
    );
    setPinned(target?.pinned ?? false);
    setPublish(target ? target.publishedAt !== null : true);
    setHidden(target?.hidden ?? false);
    setStartsAt(target?.startsAt ? formatDateTimeISO(new Date(target.startsAt)).slice(0, 16) : '');
    setEndsAt(target?.endsAt ? formatDateTimeISO(new Date(target.endsAt)).slice(0, 16) : '');
    // 「켜져 있다」의 근거는 `popupId`다 — 옵션 칸(`popup`)은 목록 응답이 함께 싣는 **값**이다
    setPopupEnabled(!!target?.popupId);
    setPopupOptions(toPopupOptions(target?.popup));
    // 기준점은 위 setState가 «반영된 뒤» 잡아야 한다 — 같은 effect에서 capture하면 이전 값이 기준이 된다
    setBaselinePending(true);
  }, [open, target]);

  // 수정 dirty 게이트(결정 2026-08-16-edit-modal-dirty-gate-standard) — 스냅샷은 저장이 보내는
  // 정규화 형태(trim)로 담는다. 생성에는 걸지 않는다(빈 폼이 기준이라 의미가 없다).
  const dirty = useDirtySnapshot(
    () => ({
      title: title.trim(),
      body: body.trim(),
      platforms: [...platforms].sort(),
      pinned,
      publish,
      hidden,
      startsAt,
      endsAt,
      popupEnabled,
      popupOptions: popupEnabled ? popupOptions : null,
    }),
    { enabled: !!target },
  );
  const { capture: captureBaseline, reset: resetBaseline } = dirty;
  useEffect(() => {
    if (!open) {
      resetBaseline();
      return;
    }
    if (!baselinePending) return;
    captureBaseline();
    setBaselinePending(false);
  }, [open, baselinePending, captureBaseline, resetBaseline]);

  const togglePlatform = (platform: OrgAnnouncementPlatform) => {
    setPlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform],
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setFormError('제목을 입력해주세요.');
      return;
    }
    if (!body.trim()) {
      setFormError('내용을 입력해주세요.');
      return;
    }
    if (platforms.length === 0) {
      setFormError('노출 화면을 하나 이상 선택해주세요. 선택하지 않으면 아무에게도 보이지 않습니다.');
      return;
    }
    const startsAtUtc = startsAt ? parseLocalToUTC(startsAt) : null;
    const endsAtUtc = endsAt ? parseLocalToUTC(endsAt) : null;
    if (startsAtUtc && endsAtUtc && startsAtUtc.getTime() >= endsAtUtc.getTime()) {
      setFormError('게시 종료가 시작보다 빠르거나 같습니다 — 노출 기간이 비어 있습니다.');
      return;
    }

    setFormError(null);
    await onSubmit({
      title: title.trim(),
      body: body.trim(),
      platforms,
      pinned,
      publish,
      // ⛔ `hidden` 은 **수정에만** 실어 보낸다 — `CreateAnnouncementDto`(= `AnnouncementContentDto`)에
      //    그 칸이 **없고** `forbidNonWhitelisted` 라, 생성에 담으면 400
      //    `VALIDATION_INVALID_INPUT: property hidden should not exist` 로 **작성 자체가 막힌다**
      //    (2026-10-27 dev 실측으로 적발 — 종전 주석이 「서버 DTO가 공통 칸으로 받는다」고 적고
      //    있었고 그것이 거짓이었다). spec 이 API 클라이언트를 mock 하므로 이 축은 **실측만** 잡는다.
      //    서버가 그렇게 가른 것은 설계다 — 숨김은 「게시된 것을 내려 두는」 행위이고, 생성 시점의
      //    같은 상태는 `publish: false`(초안)가 담당한다(`UpdateAnnouncementDto.hidden` JSDoc).
      ...(target ? { hidden } : {}),
      // 기간 칸도 `popup`과 같은 세 갈래다 — 값이면 그 시각, 빈 칸은 **생성이면 생략**(제한 없음)이고
      // **수정이면 `null`**(저장된 시각을 지운다). 수정에서 생략하면 서버가 「유지」로 읽어
      // (`resolveExposureBound`의 `undefined` 갈래) **비운 칸이 적용되지 않는다**(리뷰 1103 F2).
      ...exposureBoundField('startsAt', startsAtUtc, !!target),
      ...exposureBoundField('endsAt', endsAtUtc, !!target),
      // 「팝업으로도 알리기」 — 켜면 두 옵션을 보낸다. 끈 상태는 **생성이면 생략**(만들지 않는다)이고
      // **수정이면 `null`**(연결 팝업을 지운다)이다 — 수정에서 생략하면 「유지」라 끈 토글이 적용되지
      // 않는다(서버 `UpdateAnnouncementDto.popup` JSDoc의 세 갈래). 연결이 없는 공지의 `null`은 서버에서
      // 아무 일도 하지 않는다.
      ...(popupEnabled ? { popup: popupOptions } : target ? { popup: null } : {}),
    });
  };

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={target ? '공지 수정' : '공지 작성'}
      size="lg"
      preventClose={saving}
      isDirty={dirty.isDirty}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            취소
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={saving || (!!target && !dirty.isDirty)}>
            {saving ? '저장 중...' : '저장'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="announcement-title" className="mb-1 block text-sm font-medium text-ink-700">
            제목
          </label>
          <input
            id="announcement-title"
            type="text"
            maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm"
          />
        </div>

        <div>
          <label htmlFor="announcement-body" className="mb-1 block text-sm font-medium text-ink-700">
            내용
          </label>
          <textarea
            id="announcement-body"
            rows={6}
            maxLength={10_000}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm"
          />
        </div>

        <fieldset>
          <legend className="mb-1 block text-sm font-medium text-ink-700">노출 화면</legend>
          <div className="flex flex-wrap gap-4">
            {ORG_ANNOUNCEMENT_PLATFORMS.map((platform) => (
              <label key={platform} className="flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={platforms.includes(platform)}
                  onChange={() => togglePlatform(platform)}
                />
                {PLATFORM_LABELS[platform]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="announcement-starts-at"
              className="mb-1 block text-sm font-medium text-ink-700"
            >
              게시 시작 (선택)
            </label>
            <input
              id="announcement-starts-at"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label
              htmlFor="announcement-ends-at"
              className="mb-1 block text-sm font-medium text-ink-700"
            >
              게시 종료 (선택)
            </label>
            <input
              id="announcement-ends-at"
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              className="w-full rounded-control border border-ink-300 px-3 py-2 text-sm"
            />
            {/* 기간이 지나도 게시판에는 남는다 — 그 사실을 저작자가 알아야 「사라진다」로 오해하지 않는다 */}
            <p className="mt-1 text-xs text-ink-500">
              기간이 지나면 상단 띠에서는 빠지고, 공지 게시판에는 「지난 공지」로 남습니다.
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            상단 고정 띠로 표시
          </label>
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
            />
            지금 게시 (끄면 초안으로 저장됩니다)
          </label>
          {/* ⛔ 생성에는 띄우지 않는다 — 서버가 그 칸을 받지 않아 **눌러도 아무 일이 없다**.
              보이는 컨트롤이 적용되지 않는 것은 「존재가 상태를 뜻하는 요소」의 거짓말이라,
              조건부 렌더로 두어 손실이 초록으로 나지 않게 한다. 생성 시점에 같은 상태를
              원하면 「지금 게시」를 끄면 된다(초안). */}
          {target && (
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={hidden} onChange={(e) => setHidden(e.target.checked)} />
              숨김 (게시 상태와 무관하게 목록·띠에서 내립니다)
            </label>
          )}
          {/* 두 축이 곱이라 한쪽만 켜도 안 보인다 — 서버 조건이 `게시됨 ∧ 숨김 아님 ∧ 시작됨`이다 */}
          {target && publish && hidden && (
            <p className="text-xs text-amber-700">
              게시했지만 숨김이라 아무에게도 보이지 않습니다.
            </p>
          )}
        </div>

        {/* 「팝업으로도 알리기」 — 팝업은 이 공지의 거울이라(제목·내용·노출 화면·기간·게시·숨김을 그대로
            받는다) 여기서 고르는 것은 **팝업에만 있는 두 축**뿐이다. 뜻의 정본은 결정 기록의 ⓐ 표다. */}
        <fieldset className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-medium text-ink-700">
            <input
              type="checkbox"
              checked={popupEnabled}
              onChange={(e) => setPopupEnabled(e.target.checked)}
            />
            팝업으로도 알리기
          </label>
          {popupEnabled && (
            <div className="grid gap-3 pl-6 sm:grid-cols-2">
              <div role="radiogroup" aria-label="다시 띄우기">
                <p className="mb-1 text-xs font-medium text-ink-600">다시 띄우기</p>
                {POPUP_FREQUENCIES.map((value) => (
                  <label key={value} className="flex items-center gap-2 text-sm text-ink-700">
                    <input
                      type="radio"
                      name="announcement-popup-frequency"
                      checked={popupOptions.frequency === value}
                      onChange={() => setPopupOptions((prev) => ({ ...prev, frequency: value }))}
                    />
                    {POPUP_FREQUENCY_LABELS[value]}
                  </label>
                ))}
              </div>
              <div role="radiogroup" aria-label="보는 사람이 끄는 방법">
                <p className="mb-1 text-xs font-medium text-ink-600">보는 사람이 끄는 방법</p>
                {POPUP_OPT_OUTS.map((value) => (
                  <label key={value} className="flex items-center gap-2 text-sm text-ink-700">
                    <input
                      type="radio"
                      name="announcement-popup-opt-out"
                      checked={popupOptions.optOut === value}
                      onChange={() => setPopupOptions((prev) => ({ ...prev, optOut: value }))}
                    />
                    {POPUP_OPT_OUT_LABELS[value]}
                  </label>
                ))}
              </div>
              {/* 「다시 보지 않기」는 본문을 고쳐도 되살아나지 않는다(결정 개정 Q3) — 저작자가 그 대가를
                  고르기 전에 알아야 한다 */}
              {popupOptions.optOut === 'NEVER_AGAIN' && (
                <p className="text-xs text-ink-500 sm:col-span-2">
                  「다시 보지 않기」를 누른 사람에게는 내용을 고쳐도 다시 뜨지 않습니다. 다시
                  알려야 하면 새 공지를 쓰세요.
                </p>
              )}
            </div>
          )}
        </fieldset>

        {formError && <p className="text-sm text-rose-600">{formError}</p>}
      </div>
    </Modal>
  );
}
